import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconOnaChevronRight } from "@/assets/icons/IconOnaChevronRight";
import { IconOnaPlusCircle } from "@/assets/icons/IconOnaPlusCircle";
import { withGitpodSize } from "@/assets/icons/utils";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { Notification } from "@/components/flexkit/badge/Notification";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { Link } from "@/components/flexkit/Link";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { FlexTruncatedText } from "@/components/FlexTruncatedText";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { VisibleOnActive } from "@/components/ui/VisibleOnActive";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useEnvironmentsByProjects, type EnvironmentsGroup } from "@/hooks/use-environments-by-projects";

import { useBranch } from "@/hooks/use-git-reference";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import { usePerformance } from "@/hooks/use-performance";
import { TrackLocations } from "@/hooks/use-segment";
import { useGroupedAgentExecutions, type PlainAgentExecution } from "@/queries/agent-queries";
import { type PlainEditor } from "@/queries/editor-queries";
import {
    useStartEnvironment,
    useStopEnvironment,
    useUpdateEnvironment,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { useProject } from "@/queries/project-queries";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";
import { getDetailsURL } from "@/routes/environments/details-url";
import { EnvironmentActionsMenu } from "@/routes/environments/details/EnvironmentActionsMenu";
import { GitStatusTooltipContentFromGit } from "@/routes/environments/details/GitStatusPopOver";
import { environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import {
    agentExecutionLoading,
    agentExecutionNeedsAttention,
    agentExecutionState,
    mostRecentAgentExecution,
} from "@/utils/agent-executions";
import { getProjectDisplayName } from "@/utils/environment";
import { formatError, MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { formatChangesCount, gitStatusChangesCount } from "@/utils/git-status";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type Node } from "@react-types/shared";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import {
    EnvironmentPhase,
    ListEnvironmentsRequest_ArchivalStatus,
    UpdateEnvironmentRequest,
    UpdateEnvironmentRequest_Metadata,
    type EnvironmentGitStatus,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import { useGridList, useGridListItem, useLongPress } from "react-aria";
import { useLocation, useParams } from "react-router-dom";
import { Item, useListState, type ListState } from "react-stately";

type AgentExecutionByEnvironment = Map<string, PlainAgentExecution[]>;

export const SidebarEnvironmentList: FC = () => {
    const { markDataLoadStart, markDataLoadComplete } = usePerformance({
        component: "SidebarEnvironmentList",
    });

    // Mark data loading start
    const dataLoadStart = markDataLoadStart();

    const { data: envsByProject, isLoading } = useEnvironmentsByProjects({
        // For now we still want to show environments created by agents in the sidebar
        includeAgentCreatedEnvironments: true,
        // Only show active environments since archiving is always enabled
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });

    const { data: groupedAgentExecutions, isPending: isLoadingAgentExecutions } = useGroupedAgentExecutions();

    // Mark data loading complete when environments are loaded
    useEffect(() => {
        if (!isLoading && envsByProject) {
            markDataLoadComplete(dataLoadStart);
        }
    }, [isLoading, envsByProject, markDataLoadComplete, dataLoadStart]);

    if (isLoading || isLoadingAgentExecutions) {
        return null;
    }

    return (
        <div
            className="contents"
            data-testid="environments-list"
            data-track-location={TrackLocations.SidebarEnvironmentList}
        >
            {envsByProject.map((group) => (
                <Group
                    group={group}
                    key={`group-${group.projectId || group.repoFullName}`}
                    agentExecutionsByEnvironment={groupedAgentExecutions?.environmentScoped ?? new Map()}
                />
            ))}
        </div>
    );
};

const Group: FC<{
    group: EnvironmentsGroup;
    agentExecutionsByEnvironment: AgentExecutionByEnvironment;
}> = ({ group, agentExecutionsByEnvironment }) => {
    const { environmentId } = useParams();
    const { data: project, isLoading: isLoadingProject } = useProject(group.projectId);

    const shouldOpen = useMemo(() => {
        if (environmentId) {
            if (group.environments.some((env) => env.id === environmentId)) {
                return true;
            }
        }
        return group.environments.some(
            (env) =>
                env.status?.phase && [EnvironmentPhase.RUNNING, EnvironmentPhase.STARTING].includes(env.status.phase),
        );
    }, [environmentId, group.environments]);
    const [openState, setOpenState] = useState<"open" | "closed">(shouldOpen ? "open" : "closed");

    /**
     * When the group's `shouldOpen` computed state changes to true, we want to open the group.
     */
    useEffect(() => {
        if (shouldOpen) {
            setOpenState("open");
        }
    }, [shouldOpen]);

    const toggleOpen = useCallback(() => {
        setOpenState((current) => (current === "open" ? "closed" : "open"));
    }, []);

    const isProjectMissing = !!(!project && group.projectId && !isLoadingProject);

    return (
        <Collapsible open={openState === "open"} className="flex flex-col">
            <div
                className={cn(
                    "group flex h-9 w-full flex-row content-center items-center justify-between rounded-lg hover:bg-surface-hover",
                    "has-[>button:focus-visible]:outline has-[>button:focus-visible]:outline-1 has-[>button:focus-visible]:outline-offset-0 has-[>button:focus-visible]:outline-content-primary",
                )}
            >
                <button
                    onClick={toggleOpen}
                    aria-expanded={openState === "open"}
                    className="mr-2 flex min-w-0 grow flex-row content-center items-center focus:ring-0"
                    aria-label="Toggle Environment Group"
                    title={group.repoFullName ?? project?.metadata?.name}
                    data-tracking-id-none
                >
                    <div className="flex shrink-0 pl-1">
                        <div
                            className={cn(
                                "flex size-6 items-center justify-center transition-transform duration-150 ease-in-out",
                                openState === "open" ? "rotate-90" : "rotate-0",
                            )}
                        >
                            <IconOnaChevronRight size="base" className="text-content-muted" />
                        </div>
                    </div>
                    <div className={cn("flex min-w-0 pl-1 text-sm font-medium text-content-muted")} translate="no">
                        {group.repoFullName ? (
                            <FlexTruncatedText text={group.repoFullName} className="text-sm font-medium" />
                        ) : isProjectMissing ? (
                            "Deleted project"
                        ) : (
                            <SkeletonText size="sm" className="w-32" ready={!!project}>
                                <span className={cn("inline-block max-w-full truncate text-content-muted")}>
                                    {getProjectDisplayName(project, isProjectMissing)}
                                </span>
                            </SkeletonText>
                        )}
                    </div>
                </button>
                {project && (
                    <div className="mr-2 flex items-center opacity-0 group-focus-within:opacity-100 group-hover:opacity-100">
                        <CreateEnvironmentButton
                            project={project}
                            LeadingIcon={IconOnaPlusCircle}
                            variant={"ghost"}
                            size={"xs"}
                            className={cn("border-0 text-content-muted")}
                            onCreateSuccess={() => setOpenState("open")}
                        />
                    </div>
                )}
            </div>

            <CollapsibleContent>
                <EnvironmentsGridList group={group} agentExecutionsByEnvironment={agentExecutionsByEnvironment} />
            </CollapsibleContent>
        </Collapsible>
    );
};

const EnvironmentsGridList: FC<{
    group: EnvironmentsGroup;
    agentExecutionsByEnvironment: AgentExecutionByEnvironment;
}> = ({ group, agentExecutionsByEnvironment }) => {
    const { data: project } = useProject(group.projectId);

    const state = useListState<PlainEnvironment>({
        items: group.environments,
        children: (item) => <Item key={item.id}>{item.id}</Item>,
    });
    const ref = useRef<HTMLUListElement>(null);
    const { gridProps } = useGridList(
        {
            "aria-label":
                "Environments List for " + (project?.metadata?.name || group.repoFullName || "Unknown Project"),
        },
        state,
        ref,
    );

    const { onKeyDownCapture, ...ariaProps } = gridProps;

    return (
        <ul
            {...ariaProps}
            onKeyDownCapture={(e) => {
                // if we're in an input (such as renaming an environment), we want to prevent the grid from capturing the key events
                const target = e.target as HTMLElement;
                if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") {
                    return;
                }

                onKeyDownCapture?.(e);
            }}
            ref={ref}
            className="mx-[1px] my-1 flex flex-col gap-1"
        >
            {[...state.collection].map((item) => (
                <EnvListItem
                    key={item.key}
                    item={item}
                    state={state}
                    agentExecutions={agentExecutionsByEnvironment.get(item.value?.id ?? "") ?? []}
                />
            ))}
        </ul>
    );
};

const EnvListItem: FC<{
    item: Node<PlainEnvironment>;
    state: ListState<PlainEnvironment>;
    agentExecutions: PlainAgentExecution[];
}> = ({ item, state, agentExecutions }) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { rowProps, gridCellProps } = useGridListItem({ node: item }, state, ref);
    const location = useLocation();
    const agentExecution = useMemo(() => mostRecentAgentExecution(agentExecutions), [agentExecutions]);

    if (!item.value) {
        return null;
    }

    return (
        <li {...rowProps}>
            <div {...gridCellProps}>
                <Env
                    ref={ref}
                    env={item.value}
                    active={location.pathname.includes("/" + item.value.id)}
                    agentExecution={agentExecution}
                />
            </div>
        </li>
    );
};

const Env = React.forwardRef<
    HTMLButtonElement,
    {
        env: PlainEnvironment;
        active?: boolean;
        agentExecution?: PlainAgentExecution;
    }
>(({ env, active, agentExecution }, ref) => {
    const { selectedEditor } = useGetEditorForEnv(env.id);

    const [isRenaming, setIsRenaming] = useState(false);
    const [isContextMenuOpen, setIsContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    const [, setIsEnvDropdownOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const envName = useEnvironmentName(env, agentExecution);
    const [newName, setNewName] = useState(envName);
    const updateEnvironment = useUpdateEnvironment();
    const branchName = useBranch(env) ?? "main";
    const { toast } = useToast();
    const isPointerCoarse = useIsPointerCoarse();

    const { longPressProps } = useLongPress({
        isDisabled: !isPointerCoarse,
        onLongPress: () => {
            setIsEnvDropdownOpen(true);
        },
        accessibilityDescription: "Long press to open environment actions menu",
    });

    useEffect(() => {
        if (envName !== newName && !isRenaming) {
            // Ensure that the newName always reflects the current environment name.
            // The name can change if e.g. an agent execution is added to the environment, or its name changed.
            setNewName(envName);
        }
    }, [envName, isRenaming, newName]);

    const handleSave = useCallback(async () => {
        if (newName === envName) {
            setIsRenaming(false);
            return;
        }

        if (updateEnvironment.isPending) {
            return;
        }

        try {
            await updateEnvironment.mutateAsync({
                req: new UpdateEnvironmentRequest({
                    environmentId: env.id,
                    metadata: new UpdateEnvironmentRequest_Metadata({
                        name: newName,
                    }),
                }),
            });
            if (newName === "") {
                toast({ title: `Environment name removed` });
            } else {
                toast({ title: `Environment renamed to "${newName}"` });
            }
        } catch (err) {
            toast({
                title: "Failed to rename environment",
                description: formatError(err),
            });
        }
        setIsRenaming(false);
    }, [env.id, newName, envName, updateEnvironment, toast]);

    return (
        <EnvironmentActionsMenu
            environment={env}
            defaultEditor={selectedEditor}
            startRename={() => setIsRenaming(true)}
            renderAs="contextMenu"
            onOpenChange={setIsContextMenuOpen}
            agentExecution={agentExecution}
        >
            {isRenaming ? (
                <div
                    className={cn(
                        "group w-full rounded-lg",
                        "p-1",
                        "flex items-center justify-between",
                        active ? "bg-surface-secondary" : "hover:bg-surface-03",
                        isContextMenuOpen && !active && "bg-surface-03",
                    )}
                >
                    <div className={cn("flex min-w-0 flex-1 items-center gap-1")}>
                        <EnvironmentStatusIndicator env={env} />
                        <EnvironmentNameEditor
                            newName={newName}
                            onChange={setNewName}
                            onCancel={() => {
                                setIsRenaming(false);
                                setNewName(envName);
                            }}
                            onSave={handleSave}
                        />
                    </div>
                </div>
            ) : (
                <EnvironmentEntry
                    env={env}
                    envName={envName}
                    branchName={branchName}
                    selectedEditor={selectedEditor}
                    active={active}
                    onRename={() => setIsRenaming(true)}
                    ref={ref}
                    agentExecution={agentExecution}
                    longPressProps={isPointerCoarse ? longPressProps : undefined}
                />
            )}
        </EnvironmentActionsMenu>
    );
});
Env.displayName = "Env";

const EnvironmentEntry = React.forwardRef<
    HTMLElement,
    {
        env: PlainEnvironment;
        envName: string;
        branchName: string;
        selectedEditor: PlainEditor | undefined;
        active?: boolean;
        onRename: () => void;
        agentExecution?: PlainAgentExecution;
        longPressProps?: React.HTMLAttributes<HTMLElement>;
    }
>(({ env, envName, branchName, selectedEditor, active, onRename, agentExecution, longPressProps }, ref) => {
    const [isDropdownMenuOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const [isContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    const isActedUpon = isDropdownMenuOpen || isContextMenuOpen;

    const { isMobileViewport: isMobile } = useIsMobileViewport();

    // Check if there are uncommitted changes
    const hasChanges = (gitStatusChangesCount(env.status?.content?.git) ?? 0) > 0;

    const commonClassName = cn(
        "group w-full select-none rounded-lg p-1 pr-2",
        "flex items-center justify-between",
        // Add gap-1 when there are changes or on hover (when commands are visible)
        hasChanges && "gap-1",
        "group-hover:gap-1 group-focus-within:gap-1",
        active ? "bg-surface-hover" : "hover:bg-surface-hover",
        isActedUpon && !active && "bg-surface-hover",
    );

    if (active) {
        return (
            <div
                className={commonClassName}
                ref={ref as React.RefObject<HTMLDivElement>}
                tabIndex={-1}
                data-testid={`environment-entry-${env.id}`}
                {...longPressProps}
            >
                <div className="flex h-9 min-w-0 flex-1 items-center gap-1">
                    <EnvironmentStatusIndicator env={env} />
                    {isMobile ? (
                        <div className="flex min-w-0 flex-1 flex-col truncate text-left">
                            <EnvironmentName
                                name={envName}
                                branchName={branchName}
                                agentExecution={agentExecution}
                                className="h-5"
                            />
                            <AgentActivityStatus agentExecution={agentExecution} />
                        </div>
                    ) : (
                        <button
                            className="flex min-w-0 flex-1 flex-col truncate text-left"
                            aria-label={`Rename ${envName}`}
                            onClick={(e) => {
                                e.preventDefault();
                                onRename();
                            }}
                            tabIndex={-1}
                            data-tracking-id="rename-environment-sidebar-environment-list"
                        >
                            <EnvironmentName
                                name={envName}
                                branchName={branchName}
                                agentExecution={agentExecution}
                                className="h-5"
                            />
                            <AgentActivityStatus agentExecution={agentExecution} />
                        </button>
                    )}
                </div>

                <div className="mr-0.5 flex items-center gap-0.5">
                    <EnvironmentSidebarActions
                        env={env}
                        defaultEditor={selectedEditor}
                        startRename={onRename}
                        agentExecution={agentExecution}
                    />
                    <EnvironmentChanges git={env.status?.content?.git} />
                </div>
            </div>
        );
    }

    return (
        <Link
            ref={ref as React.RefObject<HTMLAnchorElement>}
            className={commonClassName}
            to={getDetailsURL({ environment: env })}
            aria-label={envName}
            reloadIfFrontendUpdated={true}
            data-testid={`environment-entry-${env.id}`}
            {...longPressProps}
        >
            <div className="flex h-9 min-w-0 items-center gap-1">
                <EnvironmentStatusIndicator env={env} />
                <div className="flex min-w-0 flex-1 flex-col truncate">
                    <EnvironmentName
                        name={envName}
                        branchName={branchName}
                        agentExecution={agentExecution}
                        className="h-5"
                    />
                    <AgentActivityStatus agentExecution={agentExecution} />
                </div>
            </div>

            <div
                className="mr-0.5 flex items-center gap-0.5"
                onClick={(e) => {
                    e.stopPropagation(); // prevent the link from being clicked as well
                }}
                data-tracking-id="open-environment-actions-sidebar-environment-list"
            >
                <EnvironmentSidebarActions
                    env={env}
                    defaultEditor={selectedEditor}
                    startRename={onRename}
                    agentExecution={agentExecution}
                />
                <EnvironmentChanges git={env.status?.content?.git} />
            </div>
        </Link>
    );
});
EnvironmentEntry.displayName = "EnvironmentEntry";

/// Parse todo activity format "X / Y: description"
const parseTodoActivity = (
    activity: string,
): {
    current: number;
    total: number;
    description: string;
    isTodoActivity: boolean;
    originalActivity: string;
} => {
    const todoPattern = /^(\d+)\s*\/\s*(\d+):\s*(.+)$/;
    const match = todoPattern.exec(activity);

    if (match) {
        return {
            current: parseInt(match[1], 10),
            total: parseInt(match[2], 10),
            description: match[3].trim(),
            isTodoActivity: true,
            originalActivity: activity,
        };
    }

    return {
        current: 0,
        total: 0,
        description: activity,
        isTodoActivity: false,
        originalActivity: activity,
    };
};

const TodoActivityDisplay: FC<{ activity: string }> = ({ activity }) => {
    const parsed = parseTodoActivity(activity);

    if (!parsed.isTodoActivity) {
        return <span className="block truncate font-mono text-sm text-content-muted">{parsed.description}</span>;
    }

    return (
        <div className="flex items-center gap-1">
            <div className="rounded bg-surface-muted px-1">
                <Text className="text-xs font-normal text-content-muted">
                    {parsed.current}/{parsed.total}
                </Text>
            </div>
            <span className="block truncate font-mono text-sm text-content-muted">{parsed.description}</span>
        </div>
    );
};

const AgentActivityStatus: FC<{
    agentExecution?: PlainAgentExecution;
}> = ({ agentExecution }) => {
    if (!agentExecution) {
        return null;
    }

    // If there's a current activity, show it with todo formatting if applicable
    if (agentExecution.status?.currentActivity) {
        return <TodoActivityDisplay activity={agentExecution.status.currentActivity} />;
    }

    // Otherwise show the execution state
    const state = agentExecutionState(agentExecution);
    if (!state) {
        return null;
    }

    return <span className="block truncate font-mono text-sm text-content-muted">{state}</span>;
};

export const EnvironmentName: FC<
    {
        name: string;
        branchName: string;
        size?: "sm" | "base" | "lg";
        agentExecution?: PlainAgentExecution;
    } & PropsWithClassName
> = ({ name, branchName, size = "base", agentExecution, className }) => {
    const Comp = agentExecutionLoading(agentExecution) ? TextShimmer : "span";

    return (
        <SkeletonText size={size} className="w-32" ready={!!name}>
            {name === branchName && (
                <span
                    className={cn("inline-block truncate", {
                        "text-sm": size === "sm",
                        "text-base": size === "base",
                        "text-lg": size === "lg",
                    })}
                    translate="no"
                >
                    {name}
                </span>
            )}
            {name !== branchName && (
                <Tooltip content={"Branch: " + branchName} usePortal={true}>
                    <Comp
                        className={cn(
                            "inline-block truncate",
                            {
                                "text-sm": size === "sm",
                                "text-base": size === "base",
                                "text-lg": size === "lg",
                                "font-bold": agentExecutionNeedsAttention(agentExecution),
                            },
                            className,
                        )}
                        translate="no"
                    >
                        {name}
                    </Comp>
                </Tooltip>
            )}
        </SkeletonText>
    );
};

export const EnvironmentNameEditor: FC<{
    newName: string;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
}> = ({ newName, onChange, onSave, onCancel }) => {
    const inputId = useId();

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSave();
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") {
                    e.preventDefault();
                    onCancel();
                }
            }}
            onBlur={(e: React.FocusEvent<HTMLFormElement>) => {
                // Check if the new focused element is a child of the form or if the document has focus
                const relatedTarget = e.relatedTarget as HTMLElement | null;
                const form = e.currentTarget;
                if (document.hasFocus() && (!relatedTarget || !form.contains(relatedTarget))) {
                    onSave();
                }
            }}
            className="flex flex-1 items-center gap-0.5"
        >
            <Label className="sr-only" htmlFor={inputId}>
                Custom environment name. Leave blank to use the branch name.
            </Label>
            <Input
                id={inputId}
                className="h-9 border-0 px-0 focus-visible:ring-0"
                value={newName}
                maxLength={128}
                autoComplete="off"
                type="text"
                onChange={(value) => {
                    onChange(value.target.value);
                }}
                autoFocus={true}
            />

            <EnvironmentSidebarRenameActions onSave={onSave} onCancel={onCancel} />
        </form>
    );
};

export const EnvironmentChanges: FC<{ git?: PlainMessage<EnvironmentGitStatus> } & PropsWithClassName> = ({
    git,
    className,
}) => {
    const changes = gitStatusChangesCount(git) ?? 0;

    if (changes === 0) {
        return null;
    }

    return (
        <Tooltip
            content={<GitStatusTooltipContentFromGit git={git} />}
            inverted={false}
            usePortal={true}
            side="bottom"
            align="start"
            collisionPadding={{ top: 8, bottom: 8, left: 8, right: -450 }}
            className="m-0 mt-1 p-0 shadow-md"
        >
            <Notification content={formatChangesCount(changes)} className={className} />
        </Tooltip>
    );
};

const isEnvDropdownOpenAtomFamily = atomFamily((_environmentId: string) => atom(false));
const isEnvContextMenuOpenAtom = atomFamily((_environmentId: string) => atom(false));

const EnvironmentSidebarActions: FC<{
    env: PlainEnvironment;
    defaultEditor: PlainEditor | undefined;
    startRename?: () => void;
    agentExecution?: PlainAgentExecution;
}> = ({ env, defaultEditor, startRename, agentExecution }) => {
    const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const [isContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    const isActedUpon = isDropdownMenuOpen || isContextMenuOpen;
    return (
        <VisibleOnActive className={cn("flex gap-0.5", { "not-sr-only": isActedUpon })}>
            <EnvironmentActionsMenu
                renderAs="dropdownMenu"
                environment={env}
                defaultEditor={defaultEditor}
                startRename={startRename}
                onOpenChange={setIsDropdownMenuOpen}
                agentExecution={agentExecution}
                open={isDropdownMenuOpen}
            >
                <span className="flex items-center">
                    {/* we wrap with a span because if EnvironmentActionsMenu is the direct parent of Tooltip, we would be nesting asChild-s */}
                    <Tooltip content="Open, logs, and more" usePortal>
                        <Button
                            LeadingIcon={IconEllipsisHorizontal}
                            size="xs"
                            variant="ghost"
                            type="button"
                            aria-label="Open, logs, and more"
                            className="border-0 text-content-secondary"
                            // Setting the data state so they work the same as if this button was the direct trigger.
                            data-state={isDropdownMenuOpen ? "open" : "closed"}
                            data-open={isDropdownMenuOpen ? "open" : "closed"}
                            tabIndex={-1}
                        />
                    </Tooltip>
                </span>
            </EnvironmentActionsMenu>
        </VisibleOnActive>
    );
};

const EnvironmentSidebarRenameActions: FC<{
    onSave: () => void;
    onCancel: () => void;
}> = ({ onSave, onCancel }) => {
    return (
        <div className="mr-0.5 flex items-center gap-0.5">
            <Tooltip content={`Save`} usePortal={true}>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSave();
                    }}
                    LeadingIcon={withGitpodSize(IconCheckmark2Small)}
                    size="xs"
                    variant="ghost"
                    aria-label={`Save`}
                    // deliberately not setting tabIndex to -1 here like on all other cell actions, because in renaming mode, we want these controls to remain accessible (arrow keys are not appropriate there, since the user is in a text input)
                    className="border-0 text-content-secondary"
                    data-tracking-id="confirm-save-environment-rename-sidebar-environment-list"
                />
            </Tooltip>
            <Tooltip content={`Cancel`} usePortal={true}>
                <Button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel();
                    }}
                    LeadingIcon={withGitpodSize(IconCrossSmall)}
                    size="xs"
                    variant="ghost"
                    aria-label={`Cancel`}
                    className="border-0 text-content-secondary"
                    data-tracking-id="cancel-environment-rename-sidebar-environment-list"
                />
            </Tooltip>
        </div>
    );
};

export const EnvironmentStatusIndicator: FC<{ env: PlainEnvironment }> = ({ env }) => {
    const [isDropdownMenuOpen] = useAtom(isEnvDropdownOpenAtomFamily(env.id));
    const [isContextMenuOpen] = useAtom(isEnvContextMenuOpenAtom(env.id));
    const isActedUpon = isDropdownMenuOpen || isContextMenuOpen;

    const [showLifetimeExceededModal, setShowLifetimeExceededModal] = useState(false);

    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { toast } = useToast();

    const handleStart = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            try {
                await startEnvironment.mutateAsync({ environmentId: env.id });
            } catch (error) {
                if (error instanceof MaximumEnvironmentLifetimeExceededError) {
                    setShowLifetimeExceededModal(true);
                    return;
                }
                toast({
                    title: "Failed to start environment",
                    description: formatError(error),
                });
            }
        },
        [startEnvironment, env.id, toast],
    );

    const handleStop = useCallback(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            e.preventDefault();
            try {
                await stopEnvironment.mutateAsync(env.id);
            } catch (error) {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(error),
                });
            }
        },
        [stopEnvironment, toast, env.id],
    );

    const hasReachedStablePhase = environmentHasReachedStablePhase(env);

    return (
        <>
            <div className="min-w-6">
                <EnvironmentStatusDot
                    size="lg"
                    env={env}
                    className={cn({
                        hidden: hasReachedStablePhase && isActedUpon,
                        "group-focus-within:hidden group-hover:hidden": hasReachedStablePhase,
                    })}
                />
                <div
                    className={cn("hidden size-6 group-data-[state=open]/actions-context-menu:block", {
                        block: hasReachedStablePhase && isActedUpon,
                        "group-focus-within:block group-hover:block": hasReachedStablePhase,
                    })}
                >
                    {hasReachedStablePhase && (
                        <>
                            {showStart(env) && (
                                <Tooltip content="Start Environment" usePortal={true}>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        LeadingIcon={IconPlay}
                                        onClick={handleStart}
                                        loading={startEnvironment.isPending}
                                        aria-label="Start Environment"
                                        className="size-6 border-0 text-content-secondary"
                                        tabIndex={-1} // exclude from tab order, navigation done by EnvListItem
                                        data-tracking-id="start-environment-environment-status-indicator"
                                    />
                                </Tooltip>
                            )}
                            {showStop(env) && (
                                <Tooltip content="Stop Environment" usePortal={true}>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        LeadingIcon={IconStop}
                                        onClick={handleStop}
                                        loading={stopEnvironment.isPending}
                                        aria-label="Stop Environment"
                                        className="size-6 border-0 text-content-secondary"
                                        tabIndex={-1}
                                        data-tracking-id="stop-environment-environment-status-indicator"
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
            </div>
            <EnvironmentLifetimeExceededModal
                environment={env}
                open={showLifetimeExceededModal}
                onClose={() => setShowLifetimeExceededModal(false)}
            />
        </>
    );
};
